/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Stack,
    Typography,
    TextField,
    Chip,
    CircularProgress,
    Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Iconify } from '../../../components';
import SettingsToggleRow from '../../../components/settings/SettingsToggleRow';
import StickySaveBar from '../../../components/settings/StickySaveBar';
import useDirtyForm from '../../../hooks/useDirtyForm';
import { callApi } from '../../../utils/apiCall';
import useSnackbar from '../../../hooks/useCustomSnackbar';

// ─── template variable chips ──────────────────────────────────────────────────

const VARIABLES = [
    '{{first_name}}',
    '{{practice_name}}',
    '{{next_open_time}}',
    '{{practice_phone}}',
];

// ─── default form state ───────────────────────────────────────────────────────

const DEFAULT_FORM = {
    out_of_hours_enabled: true,
    out_of_hours_message: '',
    lunch_break_message: '',
    weekend_same_message: true,
    bank_holiday_detect: true,
};

const LUNCH_BREAK_PLACEHOLDER = "We're on a short break — we'll be back at {{next_open_time}}!";

// ─── component ────────────────────────────────────────────────────────────────

export default function OutOfHoursTab({ practiceId, canEdit }) {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { isDirty, markDirty, markClean, resetTo } = useDirtyForm(DEFAULT_FORM);

    const endpoint = `/api/v1/practices/${practiceId}/ai-config/`;

    // ── load ──

    const load = useCallback(async () => {
        if (!practiceId) return;
        setLoading(true);
        try {
            const resp = await callApi(endpoint);
            if (resp.ok && resp.data) {
                const loaded = {
                    out_of_hours_enabled: resp.data.out_of_hours_enabled ?? true,
                    out_of_hours_message: resp.data.out_of_hours_message || '',
                    lunch_break_message: resp.data.lunch_break_message || '',
                    weekend_same_message: resp.data.weekend_same_message ?? true,
                    bank_holiday_detect: resp.data.bank_holiday_detect ?? true,
                };
                setForm(loaded);
                resetTo(loaded);
            }
        } catch {
            // silently ignore — form stays at defaults
        } finally {
            setLoading(false);
        }
    }, [practiceId]);

    useEffect(() => { load(); }, [load]);

    // Warn user on page leave when there are unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // ── field helpers ──

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        markDirty();
    };

    const insertVariable = (variable) => {
        set('out_of_hours_message', `${form.out_of_hours_message}${variable}`);
    };

    const insertLunchVariable = (variable) => {
        set('lunch_break_message', `${form.lunch_break_message}${variable}`);
    };

    // ── save ──

    const onSave = async () => {
        setSaving(true);
        try {
            const resp = await callApi(endpoint, 'PATCH', {
                out_of_hours_enabled: form.out_of_hours_enabled,
                out_of_hours_message: form.out_of_hours_message,
                lunch_break_message: form.lunch_break_message,
                weekend_same_message: form.weekend_same_message,
                bank_holiday_detect: form.bank_holiday_detect,
            });
            if (!resp.ok) throw new Error(resp.error || 'Failed to save');
            enqueueSnackbar('Out-of-hours settings saved', { variant: 'success' });
            markClean();
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to save out-of-hours settings', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const onDiscard = () => {
        load();
        markClean();
    };

    // ── loading state ──

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    // ─── render ────────────────────────────────────────────────────────────────

    return (
        <Box>
            {/* Master toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: form.out_of_hours_enabled ? alpha('#00A76F', 0.3) : '#e2e8f0',
                    bgcolor: form.out_of_hours_enabled ? alpha('#00A76F', 0.04) : '#f8fafc',
                    transition: 'all 0.2s',
                }}
            >
                <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify
                            icon="solar:moon-bold-duotone"
                            width={20}
                            sx={{ color: form.out_of_hours_enabled ? '#00A76F' : '#94a3b8' }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                            Auto-reply when closed
                        </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Send an automatic response to patients who message after hours
                    </Typography>
                </Stack>
                <Switch
                    checked={form.out_of_hours_enabled}
                    onChange={(e) => set('out_of_hours_enabled', e.target.checked)}
                    disabled={!canEdit}
                />
            </Box>

            {/* Message editor — only shown when enabled */}
            {form.out_of_hours_enabled && (
                <Box
                    sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        p: 2,
                        mb: 2,
                        bgcolor: '#fff',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#94a3b8',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            mb: 1.5,
                        }}
                    >
                        Out-of-Hours Message
                    </Typography>

                    <TextField
                        multiline
                        minRows={4}
                        maxRows={10}
                        fullWidth
                        placeholder="Hi {{first_name}}, thanks for your message! {{practice_name}} is currently closed. We will be back at {{next_open_time}}. For urgent queries call {{practice_phone}}."
                        value={form.out_of_hours_message}
                        onChange={(e) => set('out_of_hours_message', e.target.value)}
                        disabled={!canEdit}
                        sx={{ mb: 1.5 }}
                        inputProps={{ style: { fontSize: 13 } }}
                    />

                    {/* Variable chips */}
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.75 }}>
                        Insert variable:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {VARIABLES.map((v) => (
                            <Chip
                                key={v}
                                label={v}
                                size="small"
                                onClick={canEdit ? () => insertVariable(v) : undefined}
                                disabled={!canEdit}
                                sx={{
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    borderRadius: '6px',
                                    cursor: canEdit ? 'pointer' : 'default',
                                    bgcolor: alpha('#06b6d4', 0.08),
                                    color: '#0891b2',
                                    '&:hover': { bgcolor: canEdit ? alpha('#06b6d4', 0.16) : alpha('#06b6d4', 0.08) },
                                }}
                            />
                        ))}
                    </Stack>

                    {/* Lunch break message */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#94a3b8',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                mb: 1.5,
                            }}
                        >
                            Lunch Break Message
                        </Typography>
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={6}
                            fullWidth
                            placeholder={LUNCH_BREAK_PLACEHOLDER}
                            value={form.lunch_break_message}
                            onChange={(e) => set('lunch_break_message', e.target.value)}
                            disabled={!canEdit}
                            sx={{ mb: 1.5 }}
                            inputProps={{ style: { fontSize: 13 } }}
                        />
                        <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.75 }}>
                            Insert variable:
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                            {VARIABLES.map((v) => (
                                <Chip
                                    key={v}
                                    label={v}
                                    size="small"
                                    onClick={canEdit ? () => insertLunchVariable(v) : undefined}
                                    disabled={!canEdit}
                                    sx={{
                                        fontSize: 11,
                                        fontFamily: 'monospace',
                                        borderRadius: '6px',
                                        cursor: canEdit ? 'pointer' : 'default',
                                        bgcolor: alpha('#06b6d4', 0.08),
                                        color: '#0891b2',
                                        '&:hover': { bgcolor: canEdit ? alpha('#06b6d4', 0.16) : alpha('#06b6d4', 0.08) },
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            )}

            {/* Advanced toggles */}
            <Box
                sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Advanced
                    </Typography>
                </Box>
                <Box sx={{ px: 2, py: 0.5 }}>
                    <SettingsToggleRow
                        label="Apply on weekends"
                        description="Use the same out-of-hours message on Saturdays and Sundays"
                        checked={form.weekend_same_message}
                        onChange={canEdit ? (e) => set('weekend_same_message', e.target.checked) : undefined}
                        disabled={!canEdit}
                    />
                    <SettingsToggleRow
                        label="Detect UK bank holidays"
                        description="WhatsApp AI recognises bank holidays and adjusts the message automatically"
                        checked={form.bank_holiday_detect}
                        onChange={canEdit ? (e) => set('bank_holiday_detect', e.target.checked) : undefined}
                        disabled={!canEdit}
                    />
                </Box>
            </Box>

            {canEdit && <StickySaveBar isDirty={isDirty} onSave={onSave} onDiscard={onDiscard} saving={saving} />}
        </Box>
    );
}

OutOfHoursTab.propTypes = {
    practiceId: PropTypes.any,
    canEdit: PropTypes.bool,
};

OutOfHoursTab.defaultProps = {
    practiceId: null,
    canEdit: false,
};
